package com.kinoshazam.uiKit.trailer

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.css.CSSStyleDeclaration
import org.w3c.dom.events.Event
import kotlin.js.jsTypeOf

actual val trailerHidesUnderlyingContent: Boolean = false

@Composable
actual fun TrailerPlayer(
    youtubeUrl: String,
    expand: Float,
    muted: Boolean,
    scrim: Color,
    showBack: Boolean,
    showClose: Boolean,
    allowPull: Boolean,
    cornerRadiusDp: Float,
    onBack: () -> Unit,
    onClose: () -> Unit,
    onExpand: () -> Unit,
    onVerticalDrag: (deltaCssY: Float) -> Unit,
    onVerticalDragEnd: () -> Unit,
    modifier: Modifier,
) {
    val videoId = youtubeVideoId(youtubeUrl) ?: return
    val frame = remember { WebTrailerFrame() }
    SideEffect {
        frame.expand = expand
        frame.muted = muted
        frame.scrim = scrim.toCssRgba()
        frame.showBack = showBack
        frame.showClose = showClose
        frame.cornerDp = cornerRadiusDp
        frame.onBack = onBack
        frame.onClose = onClose
        frame.onExpand = onExpand
        frame.onDrag = onVerticalDrag
        frame.onDragEnd = onVerticalDragEnd
        frame.allowPull = allowPull
    }
    key(videoId) {
        DisposableEffect(videoId) {
            val root = document.createElement("div") as HTMLDivElement
            val canvas = document.getElementById("ComposeTarget") as? HTMLCanvasElement
            canvas?.style?.apply {
                position = "relative"
                zIndex = "1"
            }
            root.id = "kinoshka-trailer"
            root.style.apply {
                position = "fixed"
                zIndex = "0"
                backgroundColor = "#000"
                cssSet("overflow", "hidden")
                cssSet("pointer-events", "none")
            }
            val iframe = document.createElement("iframe") as HTMLIFrameElement
            iframe.setAttribute(
                "allow",
                "autoplay; encrypted-media; picture-in-picture; fullscreen",
            )
            iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin")
            iframe.style.apply {
                position = "absolute"
                border = "0"
                cssSet("pointer-events", "none")
            }
            iframe.srcdoc = youtubePlayerDocument(videoId)
            root.appendChild(iframe)

            val scrimNode = document.createElement("div") as HTMLDivElement
            scrimNode.style.apply {
                position = "absolute"
                left = "0"
                right = "0"
                top = "0"
                bottom = "0"
                cssSet("pointer-events", "none")
            }
            root.appendChild(scrimNode)

            val back = chromeButton("kinoshka-trailer-back", "\u2039") {
                frame.onBack()
            }
            val close = chromeButton("kinoshka-trailer-close", "\u00d7") {
                frame.onClose()
            }
            root.appendChild(back)
            root.appendChild(close)
            document.body?.appendChild(root)
            val onKey: (Event) -> Unit = { event ->
                val key = event.asDynamic().key as? String
                if ((key == "Escape" || key == "Esc") && frame.expand > 0.02f) {
                    event.preventDefault()
                    event.stopPropagation()
                    if (frame.expand > 0.55f) frame.onClose()
                }
            }
            val reportFailed = {
                root.style.display = "none"
                TrailerControls.fail()
            }
            window.asDynamic().ksTrailerFailed = reportFailed
            window.asDynamic().ksTrailerEscape = {
                if (frame.expand > 0.55f) frame.onClose()
            }
            window.asDynamic().ksTrailerPlaying = {
                val handler = TrailerControls.onPlaying
                if (handler != null) {
                    handler()
                    true
                } else {
                    false
                }
            }
            window.addEventListener("keydown", onKey, true)

            var raf = 0
            var lastAudible: Boolean? = null
            var lastExpanded: Boolean? = null
            var pushSoundUntil = 0.0
            var nextPushAt = 0.0
            var iframeFocusedAt = 0.0
            var tornDown = false
            fun hideNow() {
                if (tornDown) return
                tornDown = true
                window.cancelAnimationFrame(raf)
                iframe.invokePlayer("ksPause")
                root.style.display = "none"
                window.setTimeout({
                    iframe.removeAttribute("src")
                    root.remove()
                }, 800)
            }
            TrailerControls.onRelease = { hideNow() }
            fun pushSound(audible: Boolean) {
                if (audible) root.style.zIndex = "8"
                iframe.invokePlayer("ksSetAudible", audible)
            }
            TrailerControls.onAudible = { audible ->
                frame.muted = !audible
                lastAudible = audible
                pushSoundUntil = window.performance.now() + 8000.0
                nextPushAt = 0.0
                pushSound(audible)
            }
            fun tick() {
                val css = frame.cssRect()
                if (css == null || css.width < 2f || css.height < 2f) {
                    root.style.display = "none"
                } else {
                    root.style.display = "block"
                    root.style.zIndex = if (frame.expand > 0.2f) "8" else "0"
                    root.style.left = "${css.left}px"
                    root.style.top = "${css.top}px"
                    root.style.width = "${css.width}px"
                    root.style.height = "${css.height}px"
                    root.style.borderRadius = "${frame.cornerDp}px"
                    layoutEmbed(iframe, css.width, css.height)
                    val interactive = frame.expand > 0.2f
                    val hit = if (interactive) "auto" else "none"
                    root.style.cssSet("pointer-events", hit)
                    iframe.style.cssSet("pointer-events", hit)
                    scrimNode.style.opacity = "0"
                    back.style.display = if (frame.showBack) "block" else "none"
                    close.style.left = "auto"
                    close.style.right = "12px"
                    close.style.display = if (frame.showClose) "block" else "none"
                }
                val audible = !frame.muted
                val now = window.performance.now()
                if (lastAudible != audible) {
                    lastAudible = audible
                    pushSoundUntil = now + 8000.0
                    nextPushAt = now
                }
                if (now < pushSoundUntil && now >= nextPushAt) {
                    nextPushAt = now + 300.0
                    pushSound(audible)
                }
                val expanded = frame.expand > 0.55f
                if (lastExpanded != expanded && iframe.invokePlayer("ksSetExpanded", expanded)) {
                    lastExpanded = expanded
                }
                if (expanded && document.activeElement === iframe) {
                    if (iframeFocusedAt == 0.0) iframeFocusedAt = now
                    if (now - iframeFocusedAt > 500.0) {
                        iframe.blur()
                        window.focus()
                        iframeFocusedAt = 0.0
                    }
                } else {
                    iframeFocusedAt = 0.0
                }
                raf = window.requestAnimationFrame { tick() }
            }
            raf = window.requestAnimationFrame { tick() }
            onDispose {
                window.removeEventListener("keydown", onKey, true)
                window.asDynamic().ksTrailerFailed = null
                window.asDynamic().ksTrailerEscape = null
                window.asDynamic().ksTrailerPlaying = null
                if (TrailerControls.onAudible != null) TrailerControls.onAudible = null
                if (TrailerControls.onRelease != null) TrailerControls.onRelease = null
                hideNow()
            }
        }
        androidx.compose.foundation.layout.Box(
            modifier.onGloballyPositioned { coordinates ->
                val position = coordinates.positionInWindow()
                frame.x = position.x
                frame.y = position.y
                frame.width = coordinates.size.width.toFloat()
                frame.height = coordinates.size.height.toFloat()
            },
        )
    }
}

private class WebTrailerFrame {
    var x: Float = 0f
    var y: Float = 0f
    var width: Float = 0f
    var height: Float = 0f
    var expand: Float = 0f
    var muted: Boolean = true
    var scrim: String = "rgba(2,10,36,1)"
    var showBack: Boolean = false
    var showClose: Boolean = false
    var allowPull: Boolean = false
    var cornerDp: Float = 0f
    var onBack: () -> Unit = {}
    var onClose: () -> Unit = {}
    var onExpand: () -> Unit = {}
    var onDrag: (Float) -> Unit = {}
    var onDragEnd: () -> Unit = {}

    fun cssRect(): CssRect? {
        val canvas = document.getElementById("ComposeTarget") as? HTMLCanvasElement ?: return null
        val rect = canvas.getBoundingClientRect()
        val backing = canvas.width
        if (backing <= 0 || rect.width <= 0.0) return null
        val scale = (rect.width / backing).toFloat()
        return CssRect(
            left = rect.left.toFloat() + x * scale,
            top = rect.top.toFloat() + y * scale,
            width = width * scale,
            height = height * scale,
        )
    }
}

private data class CssRect(
    val left: Float,
    val top: Float,
    val width: Float,
    val height: Float,
)

private fun HTMLIFrameElement.invokePlayer(name: String, arg: Boolean? = null): Boolean {
    return try {
        val host = contentWindow?.asDynamic() ?: return false
        val method = host[name]
        if (jsTypeOf(method) != "function") return false
        if (arg == null) method.call(host) else method.call(host, arg)
        true
    } catch (_: Throwable) {
        false
    }
}

private fun chromeButton(
    id: String,
    glyph: String,
    onClick: () -> Unit,
): HTMLButtonElement {
    val button = document.createElement("button") as HTMLButtonElement
    button.id = id
    button.type = "button"
    button.textContent = glyph
    button.style.apply {
        position = "absolute"
        zIndex = "2"
        top = "12px"
        left = "12px"
        width = "44px"
        height = "44px"
        border = "0"
        borderRadius = "22px"
        backgroundColor = "rgba(2,10,36,0.55)"
        color = "#1A9FDF"
        fontSize = "28px"
        lineHeight = "40px"
        textAlign = "center"
        padding = "0"
        cssSet("cursor", "pointer")
        cssSet("pointer-events", "auto")
    }
    button.addEventListener("click", { event ->
        event.preventDefault()
        event.stopPropagation()
        onClick()
    })
    return button
}

private fun CSSStyleDeclaration.cssSet(name: String, value: String) {
    asDynamic().setProperty(name, value)
}

private fun layoutEmbed(
    iframe: HTMLIFrameElement,
    width: Float,
    height: Float,
) {
    if (width <= 0f || height <= 0f) return
    iframe.style.width = "${width}px"
    iframe.style.height = "${height}px"
    iframe.style.left = "0"
    iframe.style.top = "0"
}
